import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, X } from 'lucide-react';
import html2canvas from 'html2canvas-pro';
import { Button } from '@/components/assist/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/assist/ui/alert-dialog';

interface ScreenshotCaptureProps {
  onCapture: (file: File) => void;
  screenshot: File | null;
  onClear: () => void;
}

export function ScreenshotCapture({ onCapture, screenshot, onClear }: ScreenshotCaptureProps) {
  const { t } = useTranslation('assist');
  const [capturing, setCapturing] = useState(false);
  const [showConsent, setShowConsent] = useState(false);

  const captureScreen = async () => {
    setShowConsent(false);
    setCapturing(true);
    // Wait for React to re-render and remove the dialog overlay from the DOM
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    try {
      const mainContent = document.querySelector('main') || document.body;
      const canvas = await html2canvas(mainContent as HTMLElement, {
        ignoreElements: (element) => {
          return element.getAttribute('data-support-drawer') === 'true';
        },
      });
      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], `screenshot-${Date.now()}.png`, { type: 'image/png' });
          onCapture(file);
        }
      }, 'image/png');
    } catch (e) {
      console.error('Screenshot capture failed:', e);
    } finally {
      setCapturing(false);
    }
  };

  return (
    <div>
      {screenshot ? (
        <div className="flex items-center gap-2 p-2 rounded-md bg-muted">
          <Camera className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="text-sm truncate flex-1">{screenshot.name}</span>
          <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={onClear}>
            <X className="h-3 w-3" />
          </Button>
        </div>
      ) : (
        <Button variant="outline" size="sm" onClick={() => setShowConsent(true)} disabled={capturing}>
          <Camera className="mr-2 h-4 w-4" />
          {capturing
            ? t('support.create.capturing', 'Capturing...')
            : t('support.create.captureScreen', 'Capture screen')}
        </Button>
      )}
      <AlertDialog open={showConsent} onOpenChange={setShowConsent}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('support.create.consentTitle', 'Screenshot consent')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('support.create.consentDescription', 'A screenshot of the current page will be captured and shared with the support team. Any information visible on the screen will be included.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('support.create.consentCancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={captureScreen}>{t('support.create.consentConfirm', 'Capture & share')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
